import { STORAGE_URL } from '@/utils/const';
import type { ChatConversationDto, ChatEmployeeCard, ChatMessageDto } from '@/dtos/chat.dto';

/* -------------------------------- Mentions -------------------------------- */

/** `@[Full Name](employeeId)`: what the composer writes and the server validates. */
export const MENTION_TOKEN_RE = /@\[([^\]\n]{1,80})\]\(([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\)/g;

export const mentionToken = (name: string, employeeId: string): string => `@[${name.replace(/[\[\]()\n]/g, ' ').trim()}](${employeeId})`;

/** Mention tokens become plain "@Name" (previews, notifications, the composer's draft). */
export const plainBody = (body: string | null | undefined): string => String(body ?? '').replace(MENTION_TOKEN_RE, '@$1');

/* ---------------------------------- Links --------------------------------- */

const URL_RE = /((?:https?:\/\/|www\.)[^\s<>"'`]+)/gi;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Trailing punctuation is prose, not part of the address. */
const splitTrailingPunctuation = (raw: string): [string, string] => {
  let url = raw;
  let rest = '';
  while (url.length && /[.,;:!?)\]}'"»]/.test(url[url.length - 1])) {
    const last = url[url.length - 1];
    // keep a closing bracket that has its opening partner inside the url
    if (last === ')' && (url.match(/\(/g) ?? []).length >= (url.match(/\)/g) ?? []).length) break;
    rest = last + rest;
    url = url.slice(0, -1);
  }
  return [url, rest];
};

export const hrefFor = (url: string): string => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

/* ------------------------------- Rendering -------------------------------- */

export type BodySegment =
  | { type: 'text'; text: string }
  | { type: 'link'; text: string; href: string }
  | { type: 'mention'; name: string; employeeId: string }
  | { type: 'break' };

const splitLinks = (text: string, out: BodySegment[]): void => {
  const pattern = new RegExp(URL_RE.source, 'gi');
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    const [url, rest] = splitTrailingPunctuation(match[0]);
    if (match.index > last) out.push({ type: 'text', text: text.slice(last, match.index) });
    if (url) out.push({ type: 'link', text: url, href: hrefFor(url) });
    if (rest) out.push({ type: 'text', text: rest });
    last = match.index + match[0].length;
  }
  if (last < text.length) out.push({ type: 'text', text: text.slice(last) });
};

const splitBreaks = (text: string, out: BodySegment[]): void => {
  const lines = text.split('\n');
  lines.forEach((line, index) => {
    if (index > 0) out.push({ type: 'break' });
    if (line) splitLinks(line, out);
  });
};

/**
 * Turns a stored body into renderable pieces: text, clickable links, mention chips and
 * line breaks. The template renders each piece with text interpolation, so nothing a
 * colleague types is ever treated as HTML.
 */
export const parseBody = (body: string | null | undefined): BodySegment[] => {
  const out: BodySegment[] = [];
  const text = String(body ?? '');
  const pattern = new RegExp(MENTION_TOKEN_RE.source, 'g');
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) splitBreaks(text.slice(last, match.index), out);
    out.push({ type: 'mention', name: match[1], employeeId: match[2].toLowerCase() });
    last = match.index + match[0].length;
  }
  if (last < text.length) splitBreaks(text.slice(last), out);
  return out;
};

export const isEmailLike = (value: string): boolean => EMAIL_RE.test(value);

/* --------------------------------- People --------------------------------- */

const cap = (value: string | null | undefined): string => {
  const s = String(value ?? '').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

export const employeeDisplayName = (employee: Partial<ChatEmployeeCard> | null | undefined): string => {
  if (!employee) return 'Unknown';
  return `${cap(employee.fname)} ${cap(employee.lname)}`.trim() || 'Unknown';
};

export const employeeInitials = (employee: Partial<ChatEmployeeCard> | null | undefined): string => {
  if (!employee) return '?';
  const initials = `${String(employee.fname ?? '').charAt(0)}${String(employee.lname ?? '').charAt(0)}`.toUpperCase();
  return initials || '?';
};

export const nameInitials = (name: string | null | undefined): string => {
  const words = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((word) => word.charAt(0).toUpperCase()).join('') || '#';
};

/* ------------------------------ Conversations ----------------------------- */

export const conversationTitle = (conversation: ChatConversationDto | null | undefined): string => {
  if (!conversation) return '';
  if (conversation.type === 'direct') return conversation.peer ? employeeDisplayName(conversation.peer) : 'Direct message';
  return conversation.name || (conversation.type === 'channel' ? 'Channel' : 'Group');
};

export const conversationSubtitle = (conversation: ChatConversationDto | null | undefined): string => {
  if (!conversation) return '';
  if (conversation.type === 'direct') return conversation.peer?.isActive === false ? 'Inactive account' : '';
  const count = conversation.memberCount ?? conversation.members?.length ?? 0;
  const members = `${count} member${count === 1 ? '' : 's'}`;
  return conversation.type === 'channel' ? `Everyone · ${members}` : members;
};

/** Steady tint per name so a group keeps its colour everywhere it appears. */
export const tintFor = (seed: string | null | undefined): string => {
  const tints = ['green', 'blue', 'amber', 'purple', 'teal', 'red'];
  let hash = 0;
  for (const char of String(seed ?? '')) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return tints[hash % tints.length];
};

/* ---------------------------------- Files --------------------------------- */

export const chatFileUrl = (key: string): string => `${STORAGE_URL}/${key}`;

export const fileSizeLabel = (bytes: number | null | undefined): string => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const isImageFile = (mime: string | null | undefined, name?: string | null): boolean => {
  if (mime && /^image\/(png|jpe?g|gif|webp|bmp|svg\+xml|avif)$/i.test(mime)) return true;
  return /\.(png|jpe?g|gif|webp|bmp|avif)$/i.test(String(name ?? ''));
};

export const fileIconFor = (name: string | null | undefined, mime?: string | null): string => {
  if (isImageFile(mime, name)) return 'pi-image';
  const ext = String(name ?? '').split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return 'pi-file-pdf';
  if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) return 'pi-file-word';
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return 'pi-file-excel';
  if (['zip', 'rar', '7z', 'gz', 'tar'].includes(ext)) return 'pi-box';
  if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext) || /^video\//.test(String(mime ?? ''))) return 'pi-video';
  if (['mp3', 'wav', 'm4a', 'ogg'].includes(ext) || /^audio\//.test(String(mime ?? ''))) return 'pi-volume-up';
  return 'pi-file';
};

/* ---------------------------------- Time ---------------------------------- */

const startOfDay = (date: Date): number => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

export const sameDay = (a: string | Date, b: string | Date): boolean => startOfDay(new Date(a)) === startOfDay(new Date(b));

/** Same clock minute on the same day (local time). */
export const sameMinute = (a: string | Date, b: string | Date): boolean => {
  const da = new Date(a);
  const db = new Date(b);
  return sameDay(da, db) && da.getHours() === db.getHours() && da.getMinutes() === db.getMinutes();
};

/** Consecutive messages by one writer within a few minutes are shown as one block. */
export const closeInTime = (a: string | Date, b: string | Date, minutes = 5): boolean =>
  Math.abs(new Date(a).getTime() - new Date(b).getTime()) <= minutes * 60 * 1000;

export const timeLabel = (value: string | Date): string =>
  new Date(value).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

export const dayLabel = (value: string | Date): string => {
  const date = new Date(value);
  const today = new Date();
  const diff = Math.round((startOfDay(today) - startOfDay(date)) / (24 * 60 * 60 * 1000));
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  const sameYear = date.getFullYear() === today.getFullYear();
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
};

/** Compact "when" for lists: 10:32 AM today, "Yesterday", weekday this week, else a date. */
export const listTimeLabel = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const today = new Date();
  const diff = Math.round((startOfDay(today) - startOfDay(date)) / (24 * 60 * 60 * 1000));
  if (diff === 0) return timeLabel(date);
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return date.toLocaleDateString('en-US', { weekday: 'short' });
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(date.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' }) });
};

/** "just now", "4m", "2h", then the list label. */
export const relativeLabel = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  const ms = Date.now() - new Date(value).getTime();
  if (ms < 60 * 1000) return 'just now';
  if (ms < 60 * 60 * 1000) return `${Math.floor(ms / (60 * 1000))}m`;
  if (ms < 24 * 60 * 60 * 1000 && sameDay(value, new Date())) return `${Math.floor(ms / (60 * 60 * 1000))}h`;
  return listTimeLabel(value);
};

/* -------------------------------- Messages -------------------------------- */

export interface MessageRow {
  key: string;
  kind: 'day' | 'message' | 'unread';
  label?: string;
  message?: ChatMessageDto;
  /** First message of a block by one writer: shows avatar and name. */
  leading?: boolean;
  /** Last of a block: closes the bubble shape (and carries "Seen").*/
  trailing?: boolean;
  /** Last message of a same-writer run inside one clock minute: shows the time once for the run. */
  showsTime?: boolean;
}

/** Day separators, an unread marker and writer blocks, ready for the template. */
export const buildMessageRows = (messages: ChatMessageDto[], unreadFrom: string | null): MessageRow[] => {
  const rows: MessageRow[] = [];
  let unreadPlaced = false;
  messages.forEach((message, index) => {
    const previous = index > 0 ? messages[index - 1] : null;
    const next = index < messages.length - 1 ? messages[index + 1] : null;

    if (!previous || !sameDay(previous.createdAt, message.createdAt)) {
      rows.push({ key: `day-${message.createdAt}`, kind: 'day', label: dayLabel(message.createdAt) });
    }
    if (unreadFrom && !unreadPlaced && new Date(message.createdAt).getTime() > new Date(unreadFrom).getTime() && message.type !== 'system') {
      rows.push({ key: `unread-${message.id}`, kind: 'unread', label: 'New messages' });
      unreadPlaced = true;
    }

    const joinsPrevious = !!previous && previous.type !== 'system' && message.type !== 'system'
      && previous.senderId === message.senderId && sameDay(previous.createdAt, message.createdAt) && closeInTime(previous.createdAt, message.createdAt)
      && !(rows.length && rows[rows.length - 1].kind === 'unread');
    const joinsNext = !!next && next.type !== 'system' && message.type !== 'system'
      && next.senderId === message.senderId && sameDay(next.createdAt, message.createdAt) && closeInTime(next.createdAt, message.createdAt)
      && !(unreadFrom && !unreadPlaced && new Date(next.createdAt).getTime() > new Date(unreadFrom).getTime());

    // Messages by one writer inside the same minute share one time stamp, shown under the last of them.
    const showsTime = !joinsNext || !next || !sameMinute(next.createdAt, message.createdAt);

    rows.push({ key: message.clientId && message.pending ? `c-${message.clientId}` : message.id, kind: 'message', message, leading: !joinsPrevious, trailing: !joinsNext, showsTime });
  });
  return rows;
};

export const newClientId = (): string =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto) ? (crypto as any).randomUUID() : `c-${Date.now()}-${Math.random().toString(16).slice(2)}`;

/** True when running inside the abol HR mobile app's WebView (it plays its own sound). */
export const isInMobileApp = (): boolean => typeof window !== 'undefined' && !!(window as any).flutter_inappwebview;
